/**
 * EventPady+ — OrgSwitcher
 *
 * A dropdown rendered in the DashboardLayout header (SUPER_ADMIN only).
 * Allows the SUPER_ADMIN to switch their "viewed organisation", which
 * scopes all API calls to that tenant via the OrgSwitcherContext interceptor.
 *
 * When "Platform View" is selected, viewedOrg is cleared and the SUPER_ADMIN
 * sees cross-org data via the /platform-admin/ endpoints.
 */
import { useState, useCallback } from 'react'
import { Building2, ChevronDown, Globe, Check, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useOrgSwitcher } from '@/contexts/OrgSwitcherContext'
import { usePlatformOrganizations } from './hooks'
import { cn } from '@/lib/utils'

export function OrgSwitcher() {
  const { viewedOrg, setViewedOrg } = useOrgSwitcher()
  const [search, setSearch] = useState('')

  const { data, isLoading } = usePlatformOrganizations({
    search:   search || undefined,
    ordering: 'name',
  })

  const orgs = data?.data ?? []

  const handleSelect = useCallback(
    (org: { id: string; name: string; slug: string } | null) => {
      setViewedOrg(org)
    },
    [setViewedOrg],
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 border-dashed text-sm font-normal"
        >
          {viewedOrg ? (
            <>
              <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="max-w-[140px] truncate">{viewedOrg.name}</span>
            </>
          ) : (
            <>
              <Globe className="h-3.5 w-3.5 text-primary" />
              <span>Platform View</span>
            </>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal pb-1">
          Viewing as organisation
        </DropdownMenuLabel>

        {/* Search input */}
        <div className="px-2 pb-1">
          <input
            type="text"
            placeholder="Search organisations…"
            className="w-full rounded border border-input bg-transparent px-2 py-1 text-sm outline-none focus:border-ring"
            value={search}
            onChange={(e) => { setSearch(e.target.value) }}
            // Prevent dropdown from closing when typing
            onKeyDown={(e) => { e.stopPropagation() }}
          />
        </div>

        <DropdownMenuSeparator />

        {/* Platform-wide option */}
        <DropdownMenuItem
          className="gap-2 cursor-pointer"
          onClick={() => { handleSelect(null) }}
        >
          <Globe className={cn('h-4 w-4', !viewedOrg ? 'text-primary' : 'text-muted-foreground')} />
          <span className="flex-1">Platform View (all orgs)</span>
          {!viewedOrg && <Check className="h-3.5 w-3.5 text-primary" />}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Organisation list */}
        <div className="max-h-56 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : orgs.length === 0 ? (
            <div className="py-3 text-center text-xs text-muted-foreground">
              No organisations found
            </div>
          ) : (
            orgs.map((org) => (
              <DropdownMenuItem
                key={org.id}
                className="gap-2 cursor-pointer"
                onClick={() => {
                  handleSelect({ id: org.id, name: org.name, slug: org.slug })
                }}
              >
                <Building2
                  className={cn(
                    'h-4 w-4 shrink-0',
                    viewedOrg?.id === org.id ? 'text-primary' : 'text-muted-foreground',
                  )}
                />
                <span className="flex-1 truncate">{org.name}</span>
                {viewedOrg?.id === org.id && (
                  <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                )}
              </DropdownMenuItem>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
